// react
import { useMemo } from 'react';

// tanstack
import { useParams } from '@tanstack/react-router';

// api
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// relative
import { getRootNode } from '../utils/energyTreeFunctions';
import { useEnergyTreeNode } from './useEnergyTreeNode';

/**
 * Hook to get opening hours for the currently selected energy tree node,
 * falling back to the root node if:
 * - No node is selected, OR
 * - The selected node doesn't have opening hours defined
 *
 * @returns {Object|null} Opening hours object from node.options.openingHours, or null if not found
 */
export function useEnergyGroupOpeningHours() {
  const { energyTreeNode, nodeId } = useEnergyTreeNode();
  // const { selectedEnergyGroup } = useEnergyGroup();

  const { energyGroupId } = useParams({ strict: false });

  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    {
      skip: !energyGroupId,
    },
  );

  const openingHours = useMemo(() => {
    // If a node is selected and has opening hours, use those
    if (
      nodeId &&
      energyTreeNode?.options?.openingHours &&
      selectedEnergyGroup?.type?.toLowerCase().includes('portfolio')
    ) {
      return energyTreeNode.options.openingHours;
    }

    // Otherwise, get opening hours from the root node
    const rootNode = getRootNode(selectedEnergyGroup?.energyTreeNodes || []);
    if (rootNode?.options?.openingHours) {
      return rootNode?.options?.openingHours;
    }

    // No opening hours found anywhere
    return null;
  }, [nodeId, energyTreeNode, selectedEnergyGroup]);

  return { openingHours };
}
